import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { CrmDataProvider } from "../providers/types";
import type { CompanyNextStep, Sale } from "../types";
import { useInvalidate } from "../misc/useInvalidate";

// Ressursene som viser neste steg eller noe avledet av det.
export const NEXT_STEP_RESOURCES = ["company_next_steps", "customer_timeline"];

export type NextStepDraft = {
  mode: "step" | "none";
  description: string;
  ownerId: string;
  dueDate: string;
  reason: string;
};

export const emptyNextStepDraft = (
  ownerId?: Identifier | null,
): NextStepDraft => ({
  mode: "step",
  description: "",
  ownerId: ownerId != null ? String(ownerId) : "",
  dueDate: "",
  reason: "",
});

export const isNextStepDraftValid = (draft: NextStepDraft) =>
  draft.mode === "step"
    ? draft.description.trim() !== ""
    : draft.reason.trim() !== "";

export const nextStepInputFromDraft = (
  companyId: Identifier,
  draft: NextStepDraft,
) =>
  draft.mode === "step"
    ? {
        companyId,
        description: draft.description.trim(),
        ownerId: draft.ownerId ? Number(draft.ownerId) : null,
        dueDate: draft.dueDate || null,
      }
    : { companyId, noStepReason: draft.reason.trim() };

export const useActiveSales = () =>
  useGetList<Sale>("sales", {
    filter: { "disabled@neq": true },
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });

export const saleName = (sales: Sale[], id?: Identifier | null) => {
  if (id == null) return null;
  const sale = sales.find((s) => String(s.id) === String(id));
  return sale ? `${sale.first_name} ${sale.last_name}`.trim() : null;
};

// Feltene for neste steg: et steg med tekst, ansvarlig og dato, eller
// «ingen planlagt steg» med en begrunnelse. Brukes både i dialogen under og
// i «Registrer kontakt».
export const NextStepFields = ({
  draft,
  onChange,
  idPrefix,
}: {
  draft: NextStepDraft;
  onChange: (draft: NextStepDraft) => void;
  idPrefix: string;
}) => {
  const translate = useTranslate();
  const { data: sales = [] } = useActiveSales();
  return (
    <div className="flex flex-col gap-3">
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={draft.mode}
        onValueChange={(v) =>
          v && onChange({ ...draft, mode: v as NextStepDraft["mode"] })
        }
        className="self-start"
      >
        <ToggleGroupItem value="step" className="px-3">
          {translate("crm.next_step.mode_step", { _: "Neste steg" })}
        </ToggleGroupItem>
        <ToggleGroupItem value="none" className="px-3">
          {translate("crm.next_step.mode_none", { _: "Ingen planlagt steg" })}
        </ToggleGroupItem>
      </ToggleGroup>
      {draft.mode === "step" ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-description`}>
              {translate("crm.next_step.description", { _: "Hva skal skje" })}
            </Label>
            <Input
              id={`${idPrefix}-description`}
              value={draft.description}
              onChange={(e) =>
                onChange({ ...draft, description: e.target.value })
              }
              placeholder={translate("crm.next_step.description_placeholder", {
                _: "F.eks. send tilbud på ny nettside",
              })}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-owner`}>
                {translate("crm.next_step.owner", { _: "Ansvarlig" })}
              </Label>
              <select
                id={`${idPrefix}-owner`}
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
                value={draft.ownerId}
                onChange={(e) =>
                  onChange({ ...draft, ownerId: e.target.value })
                }
              >
                <option value="">
                  {translate("crm.next_step.no_owner", { _: "Ingen" })}
                </option>
                {sales.map((sale) => (
                  <option key={sale.id} value={String(sale.id)}>
                    {sale.first_name} {sale.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-due`}>
                {translate("crm.next_step.due_date", { _: "Dato" })}
              </Label>
              <DatePicker
                id={`${idPrefix}-due`}
                value={draft.dueDate}
                onChange={(v) => onChange({ ...draft, dueDate: v })}
                className="h-9 w-full"
              />
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-reason`}>
            {translate("crm.next_step.reason", { _: "Begrunnelse" })}
          </Label>
          <Input
            id={`${idPrefix}-reason`}
            value={draft.reason}
            onChange={(e) => onChange({ ...draft, reason: e.target.value })}
            placeholder={translate("crm.next_step.reason_placeholder", {
              _: "F.eks. venter på budsjett til januar",
            })}
          />
        </div>
      )}
    </div>
  );
};

const draftFromStep = (
  step: CompanyNextStep | undefined,
  fallbackOwner?: Identifier | null,
): NextStepDraft =>
  step
    ? {
        mode: step.description ? "step" : "none",
        description: step.description ?? "",
        ownerId:
          step.owner_id != null
            ? String(step.owner_id)
            : fallbackOwner != null
              ? String(fallbackOwner)
              : "",
        dueDate: step.due_date ?? "",
        reason: step.no_step_reason ?? "",
      }
    : emptyNextStepDraft(fallbackOwner);

// Sett eller endre kundens neste steg. Det forrige steget lukkes som
// erstattet og vises i historikken under.
export const NextStepDialog = ({
  open,
  onOpenChange,
  companyId,
  current,
  history,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: Identifier;
  current?: CompanyNextStep;
  history: CompanyNextStep[];
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const invalidate = useInvalidate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const { data: sales = [] } = useActiveSales();
  const [draft, setDraft] = useState<NextStepDraft>(() =>
    draftFromStep(current, identity?.id),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(draftFromStep(current, identity?.id));
  }, [open, current, identity?.id]);

  const save = async () => {
    if (!isNextStepDraftValid(draft)) return;
    setSaving(true);
    try {
      await dataProvider.setCompanyNextStep(
        nextStepInputFromDraft(companyId, draft),
      );
      await invalidate(...NEXT_STEP_RESOURCES);
      notify("crm.next_step.saved", {
        type: "success",
        messageArgs: { _: "Neste steg er lagret" },
      });
      onOpenChange(false);
    } catch (error) {
      notify((error as Error)?.message || "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate("crm.next_step.dialog_title", { _: "Neste steg" })}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.next_step.dialog_description", {
              _: "Det forrige steget blir liggende i historikken.",
            })}
          </DialogDescription>
        </DialogHeader>
        <NextStepFields
          draft={draft}
          onChange={setDraft}
          idPrefix="next-step"
        />
        {history.length > 0 && (
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-medium text-muted-foreground">
              {translate("crm.next_step.history", { _: "Historikk" })}
            </h3>
            <ul className="flex max-h-40 flex-col gap-1 overflow-y-auto text-sm">
              {history.map((step) => (
                <li key={step.id} className="flex items-baseline gap-2">
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {step.completed_at
                      ? new Date(step.completed_at).toLocaleDateString("nb-NO")
                      : ""}
                  </span>
                  <span
                    className={
                      step.outcome === "done"
                        ? ""
                        : "text-muted-foreground line-through"
                    }
                  >
                    {step.description ??
                      translate("crm.next_step.none_with_reason", {
                        _: "Ingen planlagt steg: %{reason}",
                        reason: step.no_step_reason ?? "",
                      })}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {step.outcome === "done"
                      ? translate("crm.next_step.outcome_done", {
                          _: "fullført",
                        })
                      : translate("crm.next_step.outcome_replaced", {
                          _: "erstattet",
                        })}
                    {saleName(sales, step.completed_by)
                      ? ` · ${saleName(sales, step.completed_by)}`
                      : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {translate("ra.action.cancel", { _: "Avbryt" })}
          </Button>
          <Button
            onClick={save}
            disabled={saving || !isNextStepDraftValid(draft)}
          >
            {translate("ra.action.save", { _: "Lagre" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
